import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { navigate } from "../../../redux/slice/layoutSlice";
import {
  useGetOrganizationsQuery,
  useGetPlansQuery,
  useGetSubscriptionsByOrganizationQuery,
  useGetSubscriptionUpgradeRequestsByOrganizationQuery,
  useApproveSubscriptionUpgradeRequestMutation,
  useRejectSubscriptionUpgradeRequestMutation,
  useActivateSubscriptionMutation,
  useCancelSubscriptionMutation,
} from "../../../redux/api";
import type { OrganizationSubscription, SubscriptionUpgradeRequest } from "../../../redux/api";
import { Modal } from "../../../components/Modal";
import { SubscriptionFormModal } from "./subscription";
import { OverviewFieldList } from "../../../components/OverviewFieldList";
import { IconPlus, IconEdit, IconCheck, IconClose } from "../../../components/icons";
import { RowMenu } from "../../../components/RowMenu";
import { subscriptionStatusTone } from "../../../utils/status";
import { todayISO, addCycle, standardPriceFor, SOURCE_LABEL } from "./subscription-helpers";

// Selection-slice key — which org this screen is scoped to (set by
// organizations.tsx before navigating here).
const ENTITY = "organization-subscriptions";

const money = (value?: number | null, currency?: string | null) =>
  typeof value === "number" ? `${currency ? `${currency} ` : ""}${value.toLocaleString()}` : "—";

const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : "—");

type ApproveForm = {
  billingCycle: string;
  amountPaid: string;
  currency: string;
  autoRenew: string;
  startDate: string;
  endDate: string;
  paymentReference: string;
  remarks: string;
};

const emptyApproveForm: ApproveForm = {
  billingCycle: "monthly",
  amountPaid: "0",
  currency: "INR",
  autoRenew: "no",
  startDate: todayISO(),
  endDate: addCycle(todayISO(), "monthly"),
  paymentReference: "",
  remarks: "",
};

// Platform Owner → one organization's subscriptions: pending upgrade
// requests (approve/reject inline), its current subscription, and the full
// history — reached by clicking an org's Plan cell in organizations.tsx.
const OrganizationSubscriptions = () => {
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector((s) => s.selection[ENTITY]);

  const { data: organizations = [] } = useGetOrganizationsQuery();
  const { data: plans = [] } = useGetPlansQuery();

  const { data: history = [], isLoading: loadingHistory } =
    useGetSubscriptionsByOrganizationQuery(organizationId ?? "", { skip: !organizationId });

  const { data: requests = [], isLoading: loadingRequests } =
    useGetSubscriptionUpgradeRequestsByOrganizationQuery(organizationId ?? "", { skip: !organizationId });

  const [approveRequest, { isLoading: approving }] = useApproveSubscriptionUpgradeRequestMutation();
  const [rejectRequest, { isLoading: rejecting }] = useRejectSubscriptionUpgradeRequestMutation();
  const [activateSubscription, { isLoading: activating }] = useActivateSubscriptionMutation();
  const [cancelSubscription, { isLoading: cancelling }] = useCancelSubscriptionMutation();

  const org = organizations.find((o) => o.id === organizationId);
  const planName = (id?: string | null) => plans.find((p) => p.id === id)?.name ?? id ?? "—";

  const pendingRequests = requests.filter((r) => r.status === "pending");

  // Most-recently-started active subscription is "current"; everything else
  // (including a still-active one that got superseded) is history.
  const sorted = [...history].sort((a, b) => (b.startDate ?? "").localeCompare(a.startDate ?? ""));
  const current = sorted.find((s) => (s.status ?? "").toLowerCase() === "active") ?? sorted[0];
  const pastHistory = sorted.filter((s) => s.id !== current?.id);

  const [approvingRequest, setApprovingRequest] = useState<SubscriptionUpgradeRequest | null>(null);
  const [approveForm, setApproveForm] = useState<ApproveForm>(emptyApproveForm);
  const [approveError, setApproveError] = useState<string | null>(null);

  const openApprove = (request: SubscriptionUpgradeRequest) => {
    const plan = plans.find((p) => p.id === request.planId);
    const price = standardPriceFor(plan, "monthly", "INR");
    setApprovingRequest(request);
    setApproveError(null);
    setApproveForm({
      ...emptyApproveForm,
      amountPaid: price != null ? String(price) : "0",
    });
  };

  const handleApproveCycleChange = (billingCycle: string) => {
    const plan = approvingRequest ? plans.find((p) => p.id === approvingRequest.planId) : undefined;
    const price = standardPriceFor(plan, billingCycle, approveForm.currency);
    setApproveForm((prev) => ({
      ...prev,
      billingCycle,
      amountPaid: price != null ? String(price) : prev.amountPaid,
      endDate: billingCycle === "custom" ? prev.endDate : addCycle(prev.startDate, billingCycle),
    }));
  };

  const handleApproveSubmit = async () => {
    if (!approvingRequest) return;
    setApproveError(null);
    try {
      await approveRequest({
        id: approvingRequest.id,
        input: {
          billingCycle: approveForm.billingCycle,
          amountPaid: Number(approveForm.amountPaid) || 0,
          currency: approveForm.currency,
          autoRenew: approveForm.autoRenew === "yes",
          startDate: approveForm.startDate,
          endDate: approveForm.endDate,
          paymentReference: approveForm.paymentReference.trim() || undefined,
          remarks: approveForm.remarks.trim() || undefined,
        },
      }).unwrap();
      setApprovingRequest(null);
    } catch (err) {
      setApproveError(
        (err as { message?: string })?.message ?? "Failed to approve request.",
      );
    }
  };

  const handleReject = async (request: SubscriptionUpgradeRequest) => {
    if (!window.confirm(`Reject the request to upgrade to ${planName(request.planId)}?`)) return;
    try {
      await rejectRequest({ id: request.id }).unwrap();
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to reject request.");
    }
  };

  const goBack = () => dispatch(navigate("/organizations"));

  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subEditingId, setSubEditingId] = useState<string | null>(null);
  const openSubscription = (id: string | null) => {
    setSubEditingId(id);
    setSubModalOpen(true);
  };

  const handleActivateOne = async (sub: OrganizationSubscription) => {
    if (!window.confirm("Activate this subscription?")) return;
    try {
      await activateSubscription(sub.id).unwrap();
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to activate subscription.");
    }
  };

  const handleCancelOne = async (sub: OrganizationSubscription) => {
    if (!window.confirm("Cancel this subscription?")) return;
    try {
      await cancelSubscription(sub.id).unwrap();
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to cancel subscription.");
    }
  };

  if (!organizationId) {
    return (
      <div className="dashboard">
        <section className="panel">
          <div className="profile-empty">
            No organization selected.{" "}
            <button type="button" className="btn btn--ghost" onClick={goBack}>
              Back to organizations
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>{org?.name ?? "Organization"}</h2>
            <p className="panel__sub">Subscriptions for this organization</p>
          </div>
          <div className="toolbar">
            <button type="button" className="btn" onClick={goBack}>
              ← Back to organizations
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openSubscription(null)}
            >
              <IconPlus /> New subscription
            </button>
          </div>
        </div>
      </section>

      {pendingRequests.length > 0 ? (
        <section className="panel">
          <div className="panel__head">
            <div>
              <h2>Pending upgrade requests</h2>
              <p className="panel__sub">{pendingRequests.length} awaiting action</p>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Plan requested</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((r) => (
                  <tr key={r.id}>
                    <td className="cell-title">{planName(r.planId)}</td>
                    <td className="cell-date">{formatDate(r.requestedAt)}</td>
                    <td>
                      <span className="row-actions">
                        <button
                          type="button"
                          className="btn btn--primary"
                          onClick={() => openApprove(r)}
                          disabled={approving || rejecting}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => handleReject(r)}
                          disabled={approving || rejecting}
                        >
                          Reject
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Current subscription</h2>
            {current ? (
              <p className="panel__sub">
                <span className={`badge badge--${subscriptionStatusTone(current.status)}`}>
                  <span className="badge__dot" />
                  {current.status}
                </span>
              </p>
            ) : null}
          </div>
          {current ? (
            <div className="row-actions">
              <button type="button" className="btn" onClick={() => openSubscription(current.id)}>
                <IconEdit size={16} /> Edit
              </button>
              {current.status !== "active" ? (
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Activate subscription"
                  title="Activate"
                  onClick={() => handleActivateOne(current)}
                  disabled={activating || cancelling}
                >
                  <IconCheck />
                </button>
              ) : null}
              {current.status !== "cancelled" ? (
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Cancel subscription"
                  title="Cancel"
                  onClick={() => handleCancelOne(current)}
                  disabled={activating || cancelling}
                >
                  <IconClose />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="panel__body">
          {loadingHistory || loadingRequests ? (
            <p>Loading…</p>
          ) : !current ? (
            <div className="profile-empty">No subscription yet for this organization.</div>
          ) : (
            <OverviewFieldList
              fields={[
                { label: "Plan", value: planName(current.planId) },
                { label: "Billing cycle", value: current.billingCycle },
                { label: "Amount paid", value: money(current.amountPaid, current.currency) },
                { label: "Auto-renew", value: current.autoRenew ? "Yes" : "No" },
                { label: "Start date", value: formatDate(current.startDate) },
                { label: "End date", value: formatDate(current.endDate) },
                {
                  label: "Created via",
                  value: current.source ? SOURCE_LABEL[current.source] ?? current.source : "—",
                },
                { label: "Payment reference", value: current.paymentReference },
                { label: "Remarks", value: current.remarks, full: true },
              ]}
            />
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>History</h2>
            <p className="panel__sub">{pastHistory.length} past subscription(s)</p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Billing</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Start</th>
                <th>End</th>
                <th>Created via</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pastHistory.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={8}>
                    No earlier subscriptions.
                  </td>
                </tr>
              ) : (
                pastHistory.map((s) => (
                  <tr key={s.id}>
                    <td className="cell-title">{planName(s.planId)}</td>
                    <td>{s.billingCycle}</td>
                    <td className="cell-date">{money(s.amountPaid, s.currency)}</td>
                    <td>
                      <span className={`badge badge--${subscriptionStatusTone(s.status)}`}>
                        <span className="badge__dot" />
                        {s.status}
                      </span>
                    </td>
                    <td className="cell-date">{formatDate(s.startDate)}</td>
                    <td className="cell-date">{formatDate(s.endDate)}</td>
                    <td>{s.source ? SOURCE_LABEL[s.source] ?? s.source : "—"}</td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "Edit",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openSubscription(s.id),
                          },
                          {
                            label: "Activate",
                            icon: <IconCheck size={16} />,
                            onSelect: () => handleActivateOne(s),
                            disabled: activating || cancelling,
                            hidden: s.status === "active",
                          },
                          {
                            label: "Cancel",
                            icon: <IconClose size={16} />,
                            onSelect: () => handleCancelOne(s),
                            danger: true,
                            disabled: activating || cancelling,
                            hidden: s.status === "cancelled",
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        open={!!approvingRequest}
        title={`Approve upgrade — ${approvingRequest ? planName(approvingRequest.planId) : ""}`}
        onClose={() => setApprovingRequest(null)}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => setApprovingRequest(null)}
              disabled={approving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleApproveSubmit}
              disabled={approving}
            >
              {approving ? "Approving…" : "Approve & activate"}
            </button>
          </>
        }
      >
        {approveError ? <div className="form-error">{approveError}</div> : null}
        <div className="form-grid">
          <label className="field">
            Billing cycle
            <select
              value={approveForm.billingCycle}
              onChange={(e) => handleApproveCycleChange(e.target.value)}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly (3 months)</option>
              <option value="half-yearly">Half-yearly (6 months)</option>
              <option value="yearly">Yearly</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <label className="field">
            Amount to charge
            <input
              type="number"
              value={approveForm.amountPaid}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, amountPaid: e.target.value }))
              }
            />
          </label>
          <label className="field">
            Currency
            <select
              value={approveForm.currency}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, currency: e.target.value }))
              }
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </label>
          <label className="field">
            Auto-renew
            <select
              value={approveForm.autoRenew}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, autoRenew: e.target.value }))
              }
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </label>
          <label className="field">
            Start date
            <input
              type="date"
              value={approveForm.startDate}
              onChange={(e) =>
                setApproveForm((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                  endDate:
                    prev.billingCycle === "custom"
                      ? prev.endDate
                      : addCycle(e.target.value, prev.billingCycle),
                }))
              }
            />
          </label>
          <label className="field">
            End date
            <input
              type="date"
              value={approveForm.endDate}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, endDate: e.target.value }))
              }
            />
          </label>
          <label className="field">
            Payment reference
            <input
              value={approveForm.paymentReference}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, paymentReference: e.target.value }))
              }
            />
          </label>
          <label className="field field--full">
            Remarks
            <textarea
              value={approveForm.remarks}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, remarks: e.target.value }))
              }
            />
          </label>
        </div>
      </Modal>

      <SubscriptionFormModal
        open={subModalOpen}
        id={subEditingId}
        onClose={() => setSubModalOpen(false)}
        initialOrganizationId={organizationId ?? undefined}
      />
    </div>
  );
};

export default OrganizationSubscriptions;
